class EchoCartGift extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    this.dialog = this.querySelector('[data-cart-gift-dialog]');
    this.openButton = this.querySelector('[data-cart-gift-open]');
    this.closeButton = this.querySelector('[data-cart-gift-close]');
    this.submitButton = this.querySelector('[data-cart-gift-submit]');
    this.removeButton = this.querySelector('[data-cart-gift-remove]');
    this.gallery = this.querySelector('[data-cart-gift-gallery]');
    this.slides = [...this.querySelectorAll('[data-cart-gift-slide]')];
    this.choices = [...this.querySelectorAll('[data-cart-gift-choice]')];
    this.selectedTitle = this.querySelector('[data-cart-gift-selected-title]');
    this.counter = this.querySelector('[data-cart-gift-counter]');
    this.message = this.querySelector('[data-cart-gift-message]');
    this.error = this.querySelector('[data-cart-gift-error]');
    this.selectedIndex = Math.max(0, this.slides.findIndex((slide) => slide.dataset.cartGiftSlide === this.dataset.currentVariant));
    this.scrollFrame = null;

    if (!this.dialog || !this.openButton || !this.submitButton || this.slides.length === 0) return;

    this.openButton.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.choices.forEach((choice, index) => {
      choice.addEventListener('click', () => this.select(index, true));
    });

    this.gallery.addEventListener('scroll', () => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        if (this.gallery.clientWidth === 0) return;
        this.select(Math.round(this.gallery.scrollLeft / this.gallery.clientWidth), false);
      });
    }, { passive: true });

    this.submitButton.addEventListener('click', () => this.save());
    this.removeButton?.addEventListener('click', () => this.remove());

    this.select(this.selectedIndex, false);
    this.dataset.ready = 'true';
  }

  open() {
    this.hideError();
    this.dialog.showModal();
    requestAnimationFrame(() => this.scrollToSelection('auto'));
  }

  select(index, shouldScroll) {
    this.selectedIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const selectedSlide = this.slides[this.selectedIndex];

    this.slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === this.selectedIndex));
    this.choices.forEach((choice, choiceIndex) => choice.setAttribute('aria-selected', String(choiceIndex === this.selectedIndex)));
    this.selectedTitle.textContent = selectedSlide.dataset.cartGiftTitle;
    this.counter.textContent = `${this.selectedIndex + 1} of ${this.slides.length}`;

    if (shouldScroll) this.scrollToSelection();
  }

  scrollToSelection(behavior = 'smooth') {
    if (!window.matchMedia('(max-width: 749px)').matches || this.gallery.clientWidth === 0) return;
    this.gallery.scrollTo({ left: this.gallery.clientWidth * this.selectedIndex, behavior });
  }

  async save() {
    const slide = this.slides[this.selectedIndex];
    const variantId = Number(slide.dataset.cartGiftSlide);
    const existingLineKey = this.dataset.existingLineKey;
    const currentVariantId = Number(this.dataset.currentVariant);
    const properties = { 'Gift message': this.message.value.trim() };

    this.setBusy(this.submitButton, true);
    this.hideError();

    try {
      let response;

      if (existingLineKey && currentVariantId === variantId) {
        response = await this.cartRequest('cart/change.js', {
          id: existingLineKey,
          quantity: 1,
          properties
        });
      } else {
        if (existingLineKey) {
          await this.cartRequest('cart/change.js', { id: existingLineKey, quantity: 0 }, false);
        }

        response = await this.cartRequest('cart/add.js', {
          items: [{ id: variantId, quantity: 1, properties }]
        });
      }

      this.dialog.close();
      await this.finishCartChange(response, 'variant:add');
    } catch (error) {
      this.showError(error.message || 'We could not update the gift wrapping. Please try again.');
    } finally {
      this.setBusy(this.submitButton, false);
    }
  }

  async remove() {
    const existingLineKey = this.dataset.existingLineKey;
    if (!existingLineKey) return;

    this.setBusy(this.removeButton, true);
    this.hideError();

    try {
      const response = await this.cartRequest('cart/change.js', { id: existingLineKey, quantity: 0 });
      await this.finishCartChange(response, 'line-item:change');
    } catch (error) {
      this.showError(error.message || 'We could not remove the gift wrapping. Please try again.');
    } finally {
      this.setBusy(this.removeButton, false);
    }
  }

  async cartRequest(path, payload, includeSections = true) {
    if (includeSections) {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections }
      }));
      payload.sections = sections;
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(`${window.Shopify.routes.root}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(payload)
    });
    const responseJson = await response.json();

    if (!response.ok) throw new Error(responseJson.description || responseJson.message || 'Something went wrong.');
    return responseJson;
  }

  async finishCartChange(response, baseEvent) {
    if (window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
      return;
    }

    const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
    cart.sections = response.sections || {};

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent,
        onSuccessDo: 'force_open_drawer',
        cart
      }
    }));
  }

  setBusy(button, busy) {
    if (!button) return;
    button.toggleAttribute('disabled', busy);
    button.setAttribute('aria-busy', String(busy));
  }

  hideError() {
    if (!this.error) return;
    this.error.hidden = true;
    this.error.textContent = '';
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = false;
  }
}

if (!window.customElements.get('echo-cart-gift')) {
  window.customElements.define('echo-cart-gift', EchoCartGift);
}
